/**
 * Arena-owned durable spatial state.
 *
 * This contract deliberately excludes Portal/Animus business projections.
 * A spatial entity may point at a Portal record by opaque identity, but run
 * status, queues, approvals, capabilities, cost and workflow data are never
 * persisted here. Those values remain disposable application projections.
 */
export const DURABLE_WORLD_SCHEMA = 'arena.world-state.record.v1' as const
export const DURABLE_WORLD_PROTOCOL = 'arena.world-state.sync.v1' as const
export const DURABLE_WORLD_SOCKET_PATH = '/api/world-state/v1' as const

export type DurableWorldSchema = typeof DURABLE_WORLD_SCHEMA
export type DurableWorldProtocol = typeof DURABLE_WORLD_PROTOCOL

export type WorldRecordKind =
  | 'world_instance'
  | 'room'
  | 'zone'
  | 'spawn_point'
  | 'membership'
  | 'placement'
  | 'annotation'
  | 'user_preference'

export type PortalDomainKind =
  | 'agent'
  | 'chat'
  | 'interaction'
  | 'resource'
  | 'run'
  | 'subject'

export interface PortalDomainReference {
  owner: 'portal'
  kind: PortalDomainKind
  /** Opaque stable identifier. It conveys no state or authorization. */
  id: string
  /** Optional optimistic identity/version token; never a workflow status. */
  revision?: string
}

export interface WorldVector3 {
  x: number
  y: number
  z: number
}

export interface WorldTransform {
  position: WorldVector3
  rotation: WorldVector3
  scale: WorldVector3
}

export interface WorldBounds {
  min: WorldVector3
  max: WorldVector3
}

export interface WorldRetention {
  class: 'standard' | 'short' | 'legal_hold'
  /** Required for short retention. Omitted for standard/legal hold records. */
  expiresAt?: string
  /** Deleted payloads are gone immediately; only this bounded marker remains. */
  tombstoneDays: number
}

export interface WorldPrivacy {
  audience: 'world' | 'room' | 'private'
  containsPersonalData: boolean
  purpose: 'collaboration' | 'personalization' | 'spatial_configuration' | 'wayfinding'
}

interface WorldRecordBase {
  schema: DurableWorldSchema
  id: string
  worldId: string
  recordVersion: number
  createdAt: string
  updatedAt: string
  retention: WorldRetention
  privacy: WorldPrivacy
}

export interface WorldInstanceRecord extends WorldRecordBase {
  kind: 'world_instance'
  defaultRoomId: string
  label: string
}

export interface WorldRoomRecord extends WorldRecordBase {
  kind: 'room'
  roomId: string
  label: string
  order: number
  defaultSpawnPointId: string
}

export interface WorldZoneRecord extends WorldRecordBase {
  kind: 'zone'
  roomId: string
  zoneId: string
  label: string
  bounds: WorldBounds
}

export interface WorldSpawnPointRecord extends WorldRecordBase {
  kind: 'spawn_point'
  roomId: string
  zoneId?: string
  transform: WorldTransform
}

export interface WorldMembershipRecord extends WorldRecordBase {
  kind: 'membership'
  /** Arena-derived, world-scoped subject reference; never a Portal user id. */
  memberRef: string
  access: 'observe' | 'participate' | 'configure'
  roomIds: string[]
}

export interface WorldPlacementRecord extends WorldRecordBase {
  kind: 'placement'
  roomId: string
  zoneId?: string
  assetRef: string
  transform: WorldTransform
  domainRef?: PortalDomainReference
}

export interface WorldAnnotationRecord extends WorldRecordBase {
  kind: 'annotation'
  roomId: string
  zoneId?: string
  body: string
  anchor: WorldVector3
  authorRef: string
}

export interface WorldUserPreferenceRecord extends WorldRecordBase {
  kind: 'user_preference'
  preferences: {
    audioMuted: boolean
    movementSensitivity: number
    reducedMotion: boolean
    showNameplates: boolean
  }
}

export type DurableWorldRecord =
  | WorldInstanceRecord
  | WorldRoomRecord
  | WorldZoneRecord
  | WorldSpawnPointRecord
  | WorldMembershipRecord
  | WorldPlacementRecord
  | WorldAnnotationRecord
  | WorldUserPreferenceRecord

export interface WorldStateInterest {
  roomIds?: string[]
  zoneIds?: string[]
  recordKinds?: WorldRecordKind[]
  /** Stable record-id continuation for disposable paged projections. */
  afterRecordId?: string
  limit: number
}

export interface WorldStateTombstone {
  recordId: string
  recordKind: WorldRecordKind
  deletedAt: string
  expiresAt: string
}

export interface WorldStateDelta {
  cursor: number
  previousCursor: number
  operation: 'upsert' | 'delete'
  record?: DurableWorldRecord
  tombstone?: WorldStateTombstone
}

export interface WorldStateResumeRequest {
  protocol: DurableWorldProtocol
  type: 'state.resume'
  messageId: string
  worldId: string
  epoch?: string
  cursor?: number
  /** Echoed from the prior response; changing interest forces a snapshot. */
  interestHash?: string
  interest: WorldStateInterest
}

export interface WorldStateUpsertRequest {
  protocol: DurableWorldProtocol
  type: 'state.upsert'
  messageId: string
  record: DurableWorldRecord
  expectedRecordVersion?: number
}

export interface WorldStateDeleteRequest {
  protocol: DurableWorldProtocol
  type: 'state.delete'
  messageId: string
  worldId: string
  recordId: string
  expectedRecordVersion: number
}

export type WorldStateClientMessage =
  | WorldStateResumeRequest
  | WorldStateUpsertRequest
  | WorldStateDeleteRequest

export interface WorldStateSnapshot {
  protocol: DurableWorldProtocol
  type: 'state.snapshot'
  worldId: string
  epoch: string
  cursor: number
  interestHash: string
  recovery: 'initial' | 'epoch_changed' | 'cursor_expired' | 'interest_changed'
  records: DurableWorldRecord[]
  nextAfterRecordId?: string
  /** Socket correlation; omitted by storage adapters and required on transport responses. */
  requestMessageId?: string
}

export interface WorldStateDeltaBatch {
  protocol: DurableWorldProtocol
  type: 'state.delta_batch'
  worldId: string
  epoch: string
  fromCursor: number
  cursor: number
  interestHash: string
  deltas: WorldStateDelta[]
  hasMore: boolean
  /** Socket correlation; omitted by storage adapters and required on transport responses. */
  requestMessageId?: string
}

export type WorldStateSyncResponse = WorldStateSnapshot | WorldStateDeltaBatch

export interface WorldStateMutationReceipt {
  protocol: DurableWorldProtocol
  type: 'state.receipt'
  requestMessageId: string
  worldId: string
  operation: 'upsert' | 'delete'
  recordId: string
  cursor: number
}

/**
 * Best-effort invalidation hint emitted after any committed world mutation.
 * It carries no record payload or authority. Clients always resume from their
 * own epoch/cursor and obtain the authoritative filtered delta from the store.
 */
export interface WorldStateChangedNotification {
  protocol: DurableWorldProtocol
  type: 'state.changed'
  worldId: string
  cursor: number
}

export interface WorldStateErrorResponse {
  protocol: DurableWorldProtocol
  type: 'state.error'
  requestMessageId?: string
  code:
    | 'protocol_mismatch'
    | 'invalid_message'
    | 'forbidden_world'
    | 'forbidden_room'
    | 'forbidden_mutation'
    | 'version_conflict'
    | 'rate_limited'
    | 'internal_error'
  message: string
  recoverable: boolean
}

export type WorldStateServerMessage =
  | WorldStateSyncResponse
  | WorldStateMutationReceipt
  | WorldStateChangedNotification
  | WorldStateErrorResponse

export interface ParseResult<T> {
  ok: boolean
  value?: T
  error?: string
}

const ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/
const REVISION = /^[A-Za-z0-9][A-Za-z0-9._:/+-]{0,255}$/
const INTEREST_HASH = /^[a-f0-9]{64}$/
const RECORD_KINDS = new Set<WorldRecordKind>([
  'world_instance', 'room', 'zone', 'spawn_point', 'membership', 'placement',
  'annotation', 'user_preference',
])
const DOMAIN_KINDS = new Set<PortalDomainKind>([
  'agent', 'chat', 'interaction', 'resource', 'run', 'subject',
])
const FORBIDDEN_BUSINESS_KEYS = new Set([
  'allowed_action', 'allowed_actions', 'approval', 'approval_state', 'capability',
  'capabilities', 'cost', 'interaction_state', 'priority', 'queue', 'queue_position',
  'run_state', 'status', 'workflow', 'workflow_state',
])

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function exactKeys(
  value: Record<string, unknown>,
  required: readonly string[],
  optional: readonly string[] = [],
): boolean {
  const allowed = new Set([...required, ...optional])
  return required.every((key) => key in value) &&
    Object.keys(value).every((key) => allowed.has(key))
}

function validId(value: unknown): value is string {
  return typeof value === 'string' && ID.test(value)
}

function boundedString(value: unknown, maximum: number): value is string {
  return typeof value === 'string' && value.length > 0 && value.length <= maximum
}

function validTimestamp(value: unknown): value is string {
  return typeof value === 'string' && Number.isFinite(Date.parse(value))
}

function validInteger(value: unknown, minimum = 0): value is number {
  return Number.isSafeInteger(value) && (value as number) >= minimum
}

function vector(value: unknown, bound = 100_000): WorldVector3 | null {
  if (!isRecord(value) || !exactKeys(value, ['x', 'y', 'z'])) return null
  const values = [value.x, value.y, value.z]
  if (values.some((part) => typeof part !== 'number' || !Number.isFinite(part) || Math.abs(part) > bound)) {
    return null
  }
  return { x: value.x as number, y: value.y as number, z: value.z as number }
}

function transform(value: unknown): WorldTransform | null {
  if (!isRecord(value) || !exactKeys(value, ['position', 'rotation', 'scale'])) return null
  const position = vector(value.position)
  const rotation = vector(value.rotation, 360_000)
  const scale = vector(value.scale, 1_000)
  if (!position || !rotation || !scale || [scale.x, scale.y, scale.z].some((part) => part <= 0)) return null
  return { position, rotation, scale }
}

function bounds(value: unknown): WorldBounds | null {
  if (!isRecord(value) || !exactKeys(value, ['min', 'max'])) return null
  const min = vector(value.min)
  const max = vector(value.max)
  if (!min || !max || min.x > max.x || min.y > max.y || min.z > max.z) return null
  return { min, max }
}

function retention(value: unknown): WorldRetention | null {
  if (!isRecord(value) || !exactKeys(value, ['class', 'tombstoneDays'], ['expiresAt'])) return null
  if (!['standard', 'short', 'legal_hold'].includes(String(value.class))) return null
  if (!validInteger(value.tombstoneDays, 1) || value.tombstoneDays > 365) return null
  if (value.class === 'short' && !validTimestamp(value.expiresAt)) return null
  if (value.class !== 'short' && value.expiresAt !== undefined) return null
  return {
    class: value.class as WorldRetention['class'],
    ...(value.expiresAt === undefined ? {} : { expiresAt: value.expiresAt as string }),
    tombstoneDays: value.tombstoneDays,
  }
}

function privacy(value: unknown): WorldPrivacy | null {
  if (!isRecord(value) || !exactKeys(value, ['audience', 'containsPersonalData', 'purpose'])) return null
  if (!['world', 'room', 'private'].includes(String(value.audience))) return null
  if (typeof value.containsPersonalData !== 'boolean') return null
  if (!['collaboration', 'personalization', 'spatial_configuration', 'wayfinding'].includes(String(value.purpose))) return null
  return value as unknown as WorldPrivacy
}

function domainReference(value: unknown): PortalDomainReference | null {
  if (!isRecord(value) || !exactKeys(value, ['owner', 'kind', 'id'], ['revision'])) return null
  if (value.owner !== 'portal' || !DOMAIN_KINDS.has(value.kind as PortalDomainKind) || !validId(value.id)) return null
  if (value.revision !== undefined && (typeof value.revision !== 'string' || !REVISION.test(value.revision))) return null
  return {
    owner: 'portal',
    kind: value.kind as PortalDomainKind,
    id: value.id,
    ...(value.revision === undefined ? {} : { revision: value.revision }),
  }
}

function base(value: Record<string, unknown>): WorldRecordBase | null {
  const parsedRetention = retention(value.retention)
  const parsedPrivacy = privacy(value.privacy)
  if (
    value.schema !== DURABLE_WORLD_SCHEMA ||
    !validId(value.id) ||
    !validId(value.worldId) ||
    !validInteger(value.recordVersion, 1) ||
    !validTimestamp(value.createdAt) ||
    !validTimestamp(value.updatedAt) ||
    Date.parse(value.updatedAt) < Date.parse(value.createdAt) ||
    !parsedRetention ||
    !parsedPrivacy
  ) return null
  return {
    schema: DURABLE_WORLD_SCHEMA,
    id: value.id,
    worldId: value.worldId,
    recordVersion: value.recordVersion,
    createdAt: value.createdAt,
    updatedAt: value.updatedAt,
    retention: parsedRetention,
    privacy: parsedPrivacy,
  }
}

const BASE_KEYS = [
  'schema', 'kind', 'id', 'worldId', 'recordVersion', 'createdAt', 'updatedAt',
  'retention', 'privacy',
] as const

/** Strict decoder used before every durable write and after every store read. */
export function parseDurableWorldRecord(input: unknown): ParseResult<DurableWorldRecord> {
  if (!isRecord(input)) return { ok: false, error: 'record must be an object' }
  if (typeof input.kind !== 'string' || !RECORD_KINDS.has(input.kind as WorldRecordKind)) {
    return { ok: false, error: 'unknown world record kind' }
  }
  if (containsCanonicalBusinessTruth(input)) {
    return { ok: false, error: 'canonical business truth is not Arena-owned world state' }
  }
  const common = base(input)
  if (!common) return { ok: false, error: 'invalid world record envelope' }
  const value = (record: DurableWorldRecord): ParseResult<DurableWorldRecord> => ({ ok: true, value: record })
  switch (input.kind) {
    case 'world_instance':
      if (!exactKeys(input, [...BASE_KEYS, 'defaultRoomId', 'label']) || !validId(input.defaultRoomId) || !boundedString(input.label, 200)) break
      return value({ ...common, kind: 'world_instance', defaultRoomId: input.defaultRoomId, label: input.label })
    case 'room':
      if (!exactKeys(input, [...BASE_KEYS, 'roomId', 'label', 'order', 'defaultSpawnPointId']) || !validId(input.roomId) || !boundedString(input.label, 200) || !validInteger(input.order) || !validId(input.defaultSpawnPointId)) break
      return value({ ...common, kind: 'room', roomId: input.roomId, label: input.label, order: input.order, defaultSpawnPointId: input.defaultSpawnPointId })
    case 'zone': {
      const parsedBounds = bounds(input.bounds)
      if (!exactKeys(input, [...BASE_KEYS, 'roomId', 'zoneId', 'label', 'bounds']) || !validId(input.roomId) || !validId(input.zoneId) || !boundedString(input.label, 200) || !parsedBounds) break
      return value({ ...common, kind: 'zone', roomId: input.roomId, zoneId: input.zoneId, label: input.label, bounds: parsedBounds })
    }
    case 'spawn_point': {
      const parsedTransform = transform(input.transform)
      if (!exactKeys(input, [...BASE_KEYS, 'roomId', 'transform'], ['zoneId']) || !validId(input.roomId) || (input.zoneId !== undefined && !validId(input.zoneId)) || !parsedTransform) break
      return value({ ...common, kind: 'spawn_point', roomId: input.roomId, ...(input.zoneId === undefined ? {} : { zoneId: input.zoneId }), transform: parsedTransform })
    }
    case 'membership':
      if (!exactKeys(input, [...BASE_KEYS, 'memberRef', 'access', 'roomIds']) || !validId(input.memberRef) || !['observe', 'participate', 'configure'].includes(String(input.access)) || !Array.isArray(input.roomIds) || input.roomIds.length > 128 || input.roomIds.some((id) => !validId(id))) break
      return value({ ...common, kind: 'membership', memberRef: input.memberRef, access: input.access as WorldMembershipRecord['access'], roomIds: [...input.roomIds] as string[] })
    case 'placement': {
      const parsedTransform = transform(input.transform)
      const parsedDomainRef = input.domainRef === undefined ? undefined : domainReference(input.domainRef)
      if (!exactKeys(input, [...BASE_KEYS, 'roomId', 'assetRef', 'transform'], ['zoneId', 'domainRef']) || !validId(input.roomId) || (input.zoneId !== undefined && !validId(input.zoneId)) || !validId(input.assetRef) || !parsedTransform || (input.domainRef !== undefined && !parsedDomainRef)) break
      return value({ ...common, kind: 'placement', roomId: input.roomId, ...(input.zoneId === undefined ? {} : { zoneId: input.zoneId }), assetRef: input.assetRef, transform: parsedTransform, ...(parsedDomainRef ? { domainRef: parsedDomainRef } : {}) })
    }
    case 'annotation': {
      const anchor = vector(input.anchor)
      if (!exactKeys(input, [...BASE_KEYS, 'roomId', 'body', 'anchor', 'authorRef'], ['zoneId']) || !validId(input.roomId) || (input.zoneId !== undefined && !validId(input.zoneId)) || !boundedString(input.body, 4_096) || !anchor || !validId(input.authorRef)) break
      return value({ ...common, kind: 'annotation', roomId: input.roomId, ...(input.zoneId === undefined ? {} : { zoneId: input.zoneId }), body: input.body, anchor, authorRef: input.authorRef })
    }
    case 'user_preference': {
      const preferences = input.preferences
      if (!exactKeys(input, [...BASE_KEYS, 'preferences']) || !isRecord(preferences) || !exactKeys(preferences, ['audioMuted', 'movementSensitivity', 'reducedMotion', 'showNameplates']) || typeof preferences.audioMuted !== 'boolean' || typeof preferences.reducedMotion !== 'boolean' || typeof preferences.showNameplates !== 'boolean' || typeof preferences.movementSensitivity !== 'number' || !Number.isFinite(preferences.movementSensitivity) || preferences.movementSensitivity < 0.1 || preferences.movementSensitivity > 10 || common.privacy.audience !== 'private') break
      return value({ ...common, kind: 'user_preference', preferences: { audioMuted: preferences.audioMuted, movementSensitivity: preferences.movementSensitivity, reducedMotion: preferences.reducedMotion, showNameplates: preferences.showNameplates } })
    }
  }
  return { ok: false, error: `invalid ${input.kind} record` }
}

/**
 * Defense in depth for future record additions. Exact record decoders are the
 * primary boundary; this recursive guard prevents smuggling canonical Portal
 * truth through a nested extension object.
 */
export function containsCanonicalBusinessTruth(input: unknown): boolean {
  if (Array.isArray(input)) return input.some(containsCanonicalBusinessTruth)
  if (!isRecord(input)) return false
  return Object.entries(input).some(([key, value]) =>
    FORBIDDEN_BUSINESS_KEYS.has(key.toLowerCase()) || containsCanonicalBusinessTruth(value),
  )
}

export function parseWorldStateInterest(input: unknown): ParseResult<WorldStateInterest> {
  if (!isRecord(input) || !exactKeys(input, ['limit'], ['roomIds', 'zoneIds', 'recordKinds', 'afterRecordId'])) return { ok: false, error: 'invalid interest shape' }
  if (!validInteger(input.limit, 1) || input.limit > 500) return { ok: false, error: 'interest limit must be 1-500' }
  const lists: Array<[unknown, number]> = [[input.roomIds, 128], [input.zoneIds, 256]]
  if (lists.some(([list, max]) => list !== undefined && (!Array.isArray(list) || list.length > max || list.some((id) => !validId(id))))) return { ok: false, error: 'invalid interest ids' }
  if (input.recordKinds !== undefined && (!Array.isArray(input.recordKinds) || input.recordKinds.length > RECORD_KINDS.size || input.recordKinds.some((kind) => typeof kind !== 'string' || !RECORD_KINDS.has(kind as WorldRecordKind)))) return { ok: false, error: 'invalid interest record kinds' }
  if (input.afterRecordId !== undefined && !validId(input.afterRecordId)) return { ok: false, error: 'invalid interest continuation' }
  return { ok: true, value: {
    ...(input.roomIds === undefined ? {} : { roomIds: [...(input.roomIds as string[])] }),
    ...(input.zoneIds === undefined ? {} : { zoneIds: [...(input.zoneIds as string[])] }),
    ...(input.recordKinds === undefined ? {} : { recordKinds: [...(input.recordKinds as WorldRecordKind[])] }),
    ...(input.afterRecordId === undefined ? {} : { afterRecordId: input.afterRecordId }),
    limit: input.limit,
  } }
}

export function parseWorldStateResume(input: unknown): ParseResult<WorldStateResumeRequest> {
  if (!isRecord(input) || !exactKeys(input, ['protocol', 'type', 'messageId', 'worldId', 'interest'], ['epoch', 'cursor', 'interestHash'])) return { ok: false, error: 'invalid state.resume shape' }
  const interest = parseWorldStateInterest(input.interest)
  if (input.protocol !== DURABLE_WORLD_PROTOCOL || input.type !== 'state.resume' || !validId(input.messageId) || !validId(input.worldId) || !interest.ok || !interest.value) return { ok: false, error: 'invalid state.resume payload' }
  const hasResume = input.epoch !== undefined || input.cursor !== undefined || input.interestHash !== undefined
  if (
    hasResume &&
    (!validId(input.epoch) || !validInteger(input.cursor) ||
      typeof input.interestHash !== 'string' || !INTEREST_HASH.test(input.interestHash))
  ) return { ok: false, error: 'epoch, cursor and interestHash must be supplied together' }
  return { ok: true, value: {
    protocol: DURABLE_WORLD_PROTOCOL,
    type: 'state.resume',
    messageId: input.messageId,
    worldId: input.worldId,
    ...(input.epoch === undefined ? {} : {
      epoch: input.epoch as string,
      cursor: input.cursor as number,
      interestHash: input.interestHash as string,
    }),
    interest: interest.value,
  } }
}

/** Strict command decoder for a future same-origin world-state transport. */
export function parseWorldStateClientMessage(input: unknown): ParseResult<WorldStateClientMessage> {
  if (!isRecord(input)) return { ok: false, error: 'world-state message must be an object' }
  if (input.type === 'state.resume') return parseWorldStateResume(input)
  if (input.protocol !== DURABLE_WORLD_PROTOCOL || !validId(input.messageId)) {
    return { ok: false, error: 'invalid world-state message envelope' }
  }
  if (input.type === 'state.upsert') {
    if (!exactKeys(input, ['protocol', 'type', 'messageId', 'record'], ['expectedRecordVersion'])) return { ok: false, error: 'invalid state.upsert shape' }
    const record = parseDurableWorldRecord(input.record)
    if (!record.ok || !record.value || (input.expectedRecordVersion !== undefined && !validInteger(input.expectedRecordVersion))) return { ok: false, error: 'invalid state.upsert payload' }
    return { ok: true, value: {
      protocol: DURABLE_WORLD_PROTOCOL,
      type: 'state.upsert',
      messageId: input.messageId,
      record: record.value,
      ...(input.expectedRecordVersion === undefined ? {} : { expectedRecordVersion: input.expectedRecordVersion }),
    } }
  }
  if (input.type === 'state.delete') {
    if (!exactKeys(input, ['protocol', 'type', 'messageId', 'worldId', 'recordId', 'expectedRecordVersion']) || !validId(input.worldId) || !validId(input.recordId) || !validInteger(input.expectedRecordVersion, 1)) return { ok: false, error: 'invalid state.delete payload' }
    return { ok: true, value: {
      protocol: DURABLE_WORLD_PROTOCOL,
      type: 'state.delete',
      messageId: input.messageId,
      worldId: input.worldId,
      recordId: input.recordId,
      expectedRecordVersion: input.expectedRecordVersion,
    } }
  }
  return { ok: false, error: 'unknown world-state message type' }
}

/** Strict browser decoder for durable snapshots and delta batches. */
export function parseWorldStateSyncResponse(input: unknown): ParseResult<WorldStateSyncResponse> {
  if (!isRecord(input) || input.protocol !== DURABLE_WORLD_PROTOCOL || !validId(input.worldId) || !validId(input.epoch) || !validInteger(input.cursor)) {
    return { ok: false, error: 'invalid world-state response envelope' }
  }
  if (input.type === 'state.snapshot') {
    if (!exactKeys(input, ['protocol', 'type', 'worldId', 'epoch', 'cursor', 'interestHash', 'recovery', 'records'], ['nextAfterRecordId', 'requestMessageId']) || typeof input.interestHash !== 'string' || !INTEREST_HASH.test(input.interestHash) || !['initial', 'epoch_changed', 'cursor_expired', 'interest_changed'].includes(String(input.recovery)) || !Array.isArray(input.records) || input.records.length > 500 || (input.nextAfterRecordId !== undefined && !validId(input.nextAfterRecordId)) || (input.requestMessageId !== undefined && !validId(input.requestMessageId))) return { ok: false, error: 'invalid state.snapshot payload' }
    const records = input.records.map(parseDurableWorldRecord)
    if (records.some((record) => !record.ok || record.value?.worldId !== input.worldId)) return { ok: false, error: 'invalid state.snapshot record' }
    return { ok: true, value: {
      protocol: DURABLE_WORLD_PROTOCOL,
      type: 'state.snapshot',
      worldId: input.worldId,
      epoch: input.epoch,
      cursor: input.cursor,
      interestHash: input.interestHash,
      recovery: input.recovery as WorldStateSnapshot['recovery'],
      records: records.map((record) => record.value!),
      ...(input.nextAfterRecordId === undefined ? {} : { nextAfterRecordId: input.nextAfterRecordId }),
      ...(input.requestMessageId === undefined ? {} : { requestMessageId: input.requestMessageId }),
    } }
  }
  if (input.type === 'state.delta_batch') {
    if (!exactKeys(input, ['protocol', 'type', 'worldId', 'epoch', 'fromCursor', 'cursor', 'interestHash', 'deltas', 'hasMore'], ['requestMessageId']) || typeof input.interestHash !== 'string' || !INTEREST_HASH.test(input.interestHash) || !validInteger(input.fromCursor) || input.fromCursor > input.cursor || !Array.isArray(input.deltas) || input.deltas.length > 500 || typeof input.hasMore !== 'boolean' || (input.requestMessageId !== undefined && !validId(input.requestMessageId))) return { ok: false, error: 'invalid state.delta_batch payload' }
    const deltas = input.deltas.map((delta) => parseWorldStateDelta(delta, input.worldId as string))
    if (deltas.some((delta) => delta === null)) return { ok: false, error: 'invalid world-state delta' }
    let previous = input.fromCursor
    for (const delta of deltas as WorldStateDelta[]) {
      if (delta.previousCursor < previous || delta.cursor !== delta.previousCursor + 1 || delta.cursor > input.cursor) return { ok: false, error: 'invalid world-state delta cursor' }
      previous = delta.cursor
    }
    return { ok: true, value: {
      protocol: DURABLE_WORLD_PROTOCOL,
      type: 'state.delta_batch',
      worldId: input.worldId,
      epoch: input.epoch,
      fromCursor: input.fromCursor,
      cursor: input.cursor,
      interestHash: input.interestHash,
      deltas: deltas as WorldStateDelta[],
      hasMore: input.hasMore,
      ...(input.requestMessageId === undefined ? {} : { requestMessageId: input.requestMessageId }),
    } }
  }
  return { ok: false, error: 'unknown world-state response type' }
}

/** Strict browser decoder for all durable-state socket responses. */
export function parseWorldStateServerMessage(input: unknown): ParseResult<WorldStateServerMessage> {
  const sync = parseWorldStateSyncResponse(input)
  if (sync.ok && sync.value) return sync
  if (!isRecord(input) || input.protocol !== DURABLE_WORLD_PROTOCOL) {
    return { ok: false, error: 'invalid world-state server envelope' }
  }
  if (input.type === 'state.receipt') {
    if (
      !exactKeys(input, [
        'protocol', 'type', 'requestMessageId', 'worldId', 'operation', 'recordId', 'cursor',
      ]) ||
      !validId(input.requestMessageId) ||
      !validId(input.worldId) ||
      !['upsert', 'delete'].includes(String(input.operation)) ||
      !validId(input.recordId) ||
      !validInteger(input.cursor, 1)
    ) return { ok: false, error: 'invalid state.receipt payload' }
    return { ok: true, value: {
      protocol: DURABLE_WORLD_PROTOCOL,
      type: 'state.receipt',
      requestMessageId: input.requestMessageId,
      worldId: input.worldId,
      operation: input.operation as WorldStateMutationReceipt['operation'],
      recordId: input.recordId,
      cursor: input.cursor,
    } }
  }
  if (input.type === 'state.changed') {
    if (
      !exactKeys(input, ['protocol', 'type', 'worldId', 'cursor']) ||
      !validId(input.worldId) ||
      !validInteger(input.cursor, 1)
    ) return { ok: false, error: 'invalid state.changed payload' }
    return { ok: true, value: {
      protocol: DURABLE_WORLD_PROTOCOL,
      type: 'state.changed',
      worldId: input.worldId,
      cursor: input.cursor,
    } }
  }
  if (input.type === 'state.error') {
    const codes = [
      'protocol_mismatch', 'invalid_message', 'forbidden_world', 'forbidden_room',
      'forbidden_mutation', 'version_conflict', 'rate_limited', 'internal_error',
    ]
    if (
      !exactKeys(input, ['protocol', 'type', 'code', 'message', 'recoverable'], ['requestMessageId']) ||
      (input.requestMessageId !== undefined && !validId(input.requestMessageId)) ||
      !codes.includes(String(input.code)) ||
      !boundedString(input.message, 512) ||
      typeof input.recoverable !== 'boolean'
    ) return { ok: false, error: 'invalid state.error payload' }
    return { ok: true, value: {
      protocol: DURABLE_WORLD_PROTOCOL,
      type: 'state.error',
      ...(input.requestMessageId === undefined ? {} : { requestMessageId: input.requestMessageId }),
      code: input.code as WorldStateErrorResponse['code'],
      message: input.message,
      recoverable: input.recoverable,
    } }
  }
  return { ok: false, error: 'unknown world-state server message type' }
}

function parseWorldStateDelta(input: unknown, worldId: string): WorldStateDelta | null {
  if (!isRecord(input) || !exactKeys(input, ['cursor', 'previousCursor', 'operation'], ['record', 'tombstone']) || !validInteger(input.cursor, 1) || !validInteger(input.previousCursor) || input.cursor !== input.previousCursor + 1) return null
  if (input.operation === 'upsert') {
    const record = parseDurableWorldRecord(input.record)
    if (!record.ok || !record.value || record.value.worldId !== worldId || input.tombstone !== undefined) return null
    return { cursor: input.cursor, previousCursor: input.previousCursor, operation: 'upsert', record: record.value }
  }
  if (input.operation !== 'delete' || input.record !== undefined || !isRecord(input.tombstone) || !exactKeys(input.tombstone, ['recordId', 'recordKind', 'deletedAt', 'expiresAt']) || !validId(input.tombstone.recordId) || typeof input.tombstone.recordKind !== 'string' || !RECORD_KINDS.has(input.tombstone.recordKind as WorldRecordKind) || !validTimestamp(input.tombstone.deletedAt) || !validTimestamp(input.tombstone.expiresAt)) return null
  return { cursor: input.cursor, previousCursor: input.previousCursor, operation: 'delete', tombstone: {
    recordId: input.tombstone.recordId,
    recordKind: input.tombstone.recordKind as WorldRecordKind,
    deletedAt: input.tombstone.deletedAt,
    expiresAt: input.tombstone.expiresAt,
  } }
}

/** Legacy placement fixture accepted only by explicit migration tooling. */
export interface LegacyWorldPlacementV0 {
  schema: 'arena.world-state.record.v0'
  kind: 'placement'
  id: string
  worldId: string
  roomId: string
  assetId: string
  x: number
  y: number
  z: number
  yaw: number
  createdAt: string
  updatedAt: string
}

export function migrateDurableWorldRecord(input: unknown): ParseResult<DurableWorldRecord> {
  const current = parseDurableWorldRecord(input)
  if (current.ok) return current
  if (!isRecord(input) || input.schema !== 'arena.world-state.record.v0' || input.kind !== 'placement' || !exactKeys(input, ['schema', 'kind', 'id', 'worldId', 'roomId', 'assetId', 'x', 'y', 'z', 'yaw', 'createdAt', 'updatedAt'])) return current
  if (!validId(input.id) || !validId(input.worldId) || !validId(input.roomId) || !validId(input.assetId) || !validTimestamp(input.createdAt) || !validTimestamp(input.updatedAt)) return { ok: false, error: 'invalid legacy placement' }
  const coordinates = [input.x, input.y, input.z, input.yaw]
  if (coordinates.some((part) => typeof part !== 'number' || !Number.isFinite(part))) return { ok: false, error: 'invalid legacy placement coordinates' }
  return parseDurableWorldRecord({
    schema: DURABLE_WORLD_SCHEMA,
    kind: 'placement',
    id: input.id,
    worldId: input.worldId,
    roomId: input.roomId,
    assetRef: input.assetId,
    transform: {
      position: { x: input.x, y: input.y, z: input.z },
      rotation: { x: 0, y: input.yaw, z: 0 },
      scale: { x: 1, y: 1, z: 1 },
    },
    recordVersion: 1,
    createdAt: input.createdAt,
    updatedAt: input.updatedAt,
    retention: { class: 'standard', tombstoneDays: 30 },
    privacy: { audience: 'world', containsPersonalData: false, purpose: 'spatial_configuration' },
  })
}
